/* eslint-disable import/prefer-default-export */
import { AnyAction } from 'redux';
import { push } from 'connected-react-router';

// Data layer
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import {
  Quotation,
  QuotationUnit,
  GeneralCatalogItem,
  ServerResponse,
} from 'data-layer-cr/dist/models';
import { THIRD_PARTY_VEHICLE } from 'data-layer-cr/dist/constants/products';

import { QuotationData } from 'data-layer-cr/dist/responseObjects';
import {
  customerWithoutNulls,
  defaultUnit,
  isSpecialPlan,
} from 'data-layer-cr/dist/functions/quotations';

// Redux
import { getLoggedRole } from 'data-layer-cr/dist/authentication';

import { AsyncAppThunk } from '../../ThunkTypes';

// Network
import processRequest from '../../../services/processRequest';

// Actions
import {
  overwriteCurrentQuotation,
  updateUsedLicense,
  updateQuotationModalIsOpen,
  updateCloningQuotationId,
  updateUsedProfile,
  updateCurrentProfile,
  updateIsLicenseAutoPx,
  updateUsedAdditionalBroker,
  updateUsedSubAgentLicense,
  closeCopyQuotationModal,
  cleanCopyQuotationData,
} from '../slice/quotationSlice';
import { setCurrentCarGeneralData } from '../../car/slice/carSlice';
import { setCurrentCustomer } from '../../customer/slice/customerslice';
import { updateCurrentPage } from '../../steppers/slice/steppersSlice';
import PricingProfileResponse from '../../../models/responseTypes/PricingProfileResponse';

import {
  REQUEST_QUOTATION_CLONE,
  REQUEST_QUOTATION_COPY,
} from '../requestIdentifiers';

// Request factory methods
import { makePricingRequestEndorsementCompare } from '../../../models/requestTypes/RequestFactory';

// Methods
import { defaultUnitAsCarItem } from '../../../functions/quotationMethods';
import {
  makeQuotationCloneError,
  appliesNewAutoPxDiscount,
  updateQuotationUnits,
  haveQuotationProfile,
} from './helperMethods';
import customerFromQuotation from '../../../functions/customerItemFromQuotation';

// Request/Response types
import PricingPreviewRequest from '../../../models/requestTypes/PricingPreviewRequest';
import {
  APPLICATION_NAME,
  UNIQUE_PRICING_REQUEST_ID,
} from './makeUpdateActions';

const pricingVehicleEndpoint = 'Pricing/VehiclePricing';
const quotationPricePerUnitEndpoint = 'Quotation/priceperunit';

/**
 * Clones the quotation identified by the provided id.
 *
 * @param quotationId - The quotation id that we want to clone
 */
export const cloneAndEditQuotation =
  (quotationId: number): AsyncAppThunk =>
  async (dispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_QUOTATION_CLONE, 'requesting'));

    /**
     * Cloning quotation id is used to check which card needs to render the "loading" spinner,
     * as just checking for the request status causes all cards to display the spinner
     */
    dispatch(updateCloningQuotationId(quotationId));
    try {
      /** Request the source quotation and set it to the store */
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `Quotation/getQuotationById?id=${quotationId}&applicationName=${APPLICATION_NAME}`,
        customErrorMessage: makeQuotationCloneError,
      });

      const { quotation } = response.data;

      /** Get all profiles that one user has */
      const userProfiles = await processRequest<
        ServerResponse<PricingProfileResponse[]>
      >({
        method: 'GET',
        url: 'Pricing/user/profiles',
      });

      const hasCloningProfile = haveQuotationProfile(
        userProfiles.data,
        quotation.quotationProfile,
      );

      /** Checks if the user has the cloning profile, otherwise we return default vehicle. */
      const quotationCoverages = hasCloningProfile
        ? defaultUnit(quotation).coverages
        : [];

      const requestData: PricingPreviewRequest =
        makePricingRequestEndorsementCompare(quotation, quotationCoverages);

      const pricingResponse = await processRequest<ServerResponse<Quotation>>({
        method: 'POST',
        url: pricingVehicleEndpoint,
        data: requestData,
        uniqueIdentifier: UNIQUE_PRICING_REQUEST_ID,
      });

      /** Since we are cloning a quotation, we must re-check autoPx discount eligibility */
      let appliesAutoPx = true;
      if (getLoggedRole() === 'ASE') {
        const filterOption = `?filter[where][code][eq]=${quotation.brokerLicense}`;
        const autoPxResult = await processRequest<
          NetworkResponse<GeneralCatalogItem[]>
        >({
          method: 'GET',
          url: `Catalog/policy/brokerlicenses${filterOption}`,
        });

        const hasLicenseAutoPx = autoPxResult.data.length > 0;

        dispatch(updateIsLicenseAutoPx(hasLicenseAutoPx));

        appliesAutoPx =
          hasLicenseAutoPx ||
          (hasCloningProfile && isSpecialPlan(quotation.quotationProfile));
      }

      // ? We are keeping the function because in december 31, 2021 conditions might change
      const appliesForNewAutoPxDiscount =
        appliesNewAutoPxDiscount(appliesAutoPx);

      // Pricing is always returning isNewCar false, so we have to set it to the cloning quotation.
      pricingResponse.data.units[0].unit.isNewCar =
        defaultUnit(quotation).unit.isNewCar;

      quotation.units = pricingResponse.data.units;

      if (appliesForNewAutoPxDiscount) {
        /** On create quotation is always just 1 unit */
        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: quotationPricePerUnitEndpoint,
          data: quotation.units,
        });

        quotation.units = updateQuotationUnits(
          quotation.units,
          autoPxUnits.data,
        );
      }

      /**
       * We have to overwrite the profile given by pricing because we don't know if
       * the current profile has that plan.
       */
      quotation.quotationProfile = pricingResponse.data.quotationProfile;

      /**
       * Since quotations should always be created with multiple options, we need
       * to overwrite these properties
       *
       * We are also overwriting quotationProfile, since we want the prices from
       * the pricing request.
       *
       */

      dispatch(
        overwriteCurrentQuotation({
          ...quotation,
          hasMultipleOptions: quotation.productId !== THIRD_PARTY_VEHICLE,
          isAutoPx: appliesAutoPx,
          user: {
            ...quotation.user,
            /**
             * Since we are cloning, the user should be the current logged user, to apply AutoPx discount
             * it happens when we want to clone from ASE to AGE
             *  */
            role: getLoggedRole(),
          },
        }),
      );

      const driver = customerFromQuotation({
        ...quotation,
        customer: customerWithoutNulls(quotation),
      });
      dispatch(setCurrentCustomer(driver));

      /** Update car information */
      const quotationUnit = defaultUnitAsCarItem(quotation);

      dispatch(setCurrentCarGeneralData(quotationUnit));

      /** Update used license */
      dispatch(updateUsedLicense(quotation.brokerLicense));

      /** Update used SubAgentlicense */
      dispatch(updateUsedSubAgentLicense(quotation.agencyId));

      /** Update used profile */
      dispatch(updateUsedProfile(quotation.quotationProfile.id));

      /** Update used additional brokers */
      dispatch(
        updateUsedAdditionalBroker(
          quotation.additionalBrokers.map((broker) => broker.brokerLicense),
        ),
      );

      /** Force the "modal" to be open on the last page */
      dispatch(updateCurrentPage(3));
      dispatch(updateQuotationModalIsOpen(true));

      /** Reset the current "cloning" quotation id and navigate to dynamicQuotation page */
      dispatch(updateCloningQuotationId(-1));
      dispatch(push('/dynamicQuotation'));
      return dispatch(setRequestStatus(REQUEST_QUOTATION_CLONE, 'success'));
    } catch (e) {
      dispatch(updateCloningQuotationId(-1));
      return dispatch(setRequestStatus(REQUEST_QUOTATION_CLONE, 'failure'));
    }
  };

export const copyQuotationInformation =
  (): AsyncAppThunk =>
  async (dispatch, getState): Promise<AnyAction> => {
    const { quotationHash } = getState().quotation.copyQuotationData;
    dispatch(setRequestStatus(REQUEST_QUOTATION_COPY, 'requesting'));

    /**
     * Cloning quotation id is used to check which card needs to render the "loading" spinner,
     * as just checking for the request status causes all cards to display the spinner
     */
    dispatch(closeCopyQuotationModal());
    try {
      /** Request the source quotation and set it to the store */
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `quotation?hash=${quotationHash}`,
      });

      const { quotation } = response.data;

      /** Update License information */
      const currentProfile: PricingProfileResponse = {
        productId: quotation.productId,
        numberPolicyMatriz: '',
        typeOfCoverage: '',
        description: quotation.quotationProfile.description,
        id: quotation.quotationProfile.id,
        type: quotation.quotationProfile.type || '',
        typeOfMoney: quotation.quotationProfile.typeOfMoney || '',
      };
      dispatch(updateUsedLicense(quotation.brokerLicense));
      dispatch(updateUsedProfile(quotation.quotationProfile.id));
      dispatch(updateCurrentProfile(currentProfile));
      dispatch(updateUsedAdditionalBroker([]));
      dispatch(updateUsedSubAgentLicense(quotation.agencyId));

      /** Update Customer information */
      const driver = customerFromQuotation({
        ...quotation,
        customer: customerWithoutNulls(quotation),
      });
      dispatch(setCurrentCustomer(driver));

      /** Update Car information */
      const quotationUnit = defaultUnitAsCarItem(quotation);
      dispatch(setCurrentCarGeneralData(quotationUnit));

      /** Force the "modal" to be open on the last page */
      dispatch(updateCurrentPage(1));
      dispatch(updateQuotationModalIsOpen(true));

      return dispatch(setRequestStatus(REQUEST_QUOTATION_COPY, 'success'));
    } catch (e) {
      dispatch(cleanCopyQuotationData());
      return dispatch(setRequestStatus(REQUEST_QUOTATION_COPY, 'failure'));
    }
  };
